
import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import PageHero from '../components/common/PageHero';
import ProcessSteps from '../components/common/ProcessSteps';
import CTASection from '../components/common/CTASection';
import CanauxSection from '../components/communication-360/CanauxSection';
import ExemplesSection from '../components/communication-360/ExemplesSection';
import { TrendingUp, BarChart3, Target, Calendar, CheckCircle, Globe, Megaphone, Users } from 'lucide-react';

const Communication360Page = () => {
  const processSteps = [
    {
      step: "01",
      title: "Audit Global",
      description: "Analyse de tous vos canaux de communication existants",
      icon: BarChart3
    },
    {
      step: "02", 
      title: "Stratégie Intégrée",
      description: "Création d'une stratégie cohérente sur tous les canaux",
      icon: Target
    },
    {
      step: "03",
      title: "Planning & Coordination",
      description: "Planification et synchronisation de toutes les actions",
      icon: Calendar
    },
    {
      step: "04",
      title: "Déploiement & Suivi",
      description: "Mise en œuvre coordonnée et optimisation continue",
      icon: TrendingUp
    }
  ];

  const avantages = [
    {
      title: "Cohérence maximale",
      description: "Un message unifié sur tous vos points de contact",
      icon: Target,
      stat: "100%"
    },
    {
      title: "Portée amplifiée",
      description: "Touchez votre audience sur tous les canaux qu'elle fréquente",
      icon: Megaphone,
      stat: "+300%"
    },
    {
      title: "ROI optimisé",
      description: "Synergie entre les canaux pour un retour sur investissement maximisé",
      icon: TrendingUp,
      stat: "+250%"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <PageHero
        badge={{ icon: TrendingUp, text: "Communication 360°" }}
        title='Une <span class="font-semibold text-blue-600">synergie</span> parfaite'
        subtitle="sur tous les canaux"
        description="Je déploie votre message de manière cohérente et impactante sur l'ensemble de vos canaux de communication. Une approche intégrée pour maximiser votre portée et votre impact."
      />

      {/* Qu'est-ce que la communication 360° */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-light text-navy-900 mb-6">
                  Qu'est-ce que la <span className="font-semibold text-blue-600">communication 360°</span> ?
                </h2>
                <p className="text-lg text-navy-600 font-light mb-6 leading-relaxed">
                  La communication 360° consiste à déployer un message cohérent et coordonné sur l'ensemble des canaux de communication disponibles. 
                  Cette approche holistique garantit une présence omnicanale et maximise l'impact de vos actions.
                </p>
                <p className="text-lg text-navy-600 font-light mb-8 leading-relaxed">
                  En synchronisant tous vos points de contact, vous créez une expérience fluide et mémorable pour votre audience.
                </p>
                <ul className="space-y-3">
                  {["Cohérence sur tous les canaux", "Amplification du message", "Optimisation des synergies", "Mesure globale des performances"].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-navy-600">
                      <CheckCircle className="text-blue-600 flex-shrink-0" size={20} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-pink-50 p-12">
                <div className="text-center">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-blue-600 text-white p-4 text-center">
                      <Globe size={24} className="mx-auto mb-2" />
                      <span className="text-sm">Digital</span>
                    </div>
                    <div className="bg-pink-500 text-white p-4 text-center">
                      <Megaphone size={24} className="mx-auto mb-2" />
                      <span className="text-sm">Presse</span>
                    </div>
                    <div className="bg-navy-700 text-white p-4 text-center">
                      <Calendar size={24} className="mx-auto mb-2" />
                      <span className="text-sm">Événements</span>
                    </div>
                    <div className="bg-blue-500 text-white p-4 text-center">
                      <Users size={24} className="mx-auto mb-2" />
                      <span className="text-sm">Interne</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-navy-900 mb-2">Tous connectés</h3>
                  <p className="text-navy-600 font-light text-sm">Une orchestration parfaite</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CanauxSection />

      <ProcessSteps
        title='Ma <span class="font-semibold text-blue-600">méthodologie</span> 360°'
        subtitle="Une approche structurée pour une communication parfaitement orchestrée"
        steps={processSteps}
      />

      {/* Avantages avec stats */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-navy-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-light text-white mb-6">
                Pourquoi choisir une approche <span className="font-semibold text-pink-400">360°</span> ?
              </h2>
              <p className="text-xl text-navy-200 font-light">
                Des résultats mesurables grâce à une stratégie intégrée
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {avantages.map((avantage, index) => (
                <div key={index} className="text-center bg-white/10 p-8 hover:bg-white/20 transition-colors duration-300">
                  <avantage.icon className="text-pink-400 mx-auto mb-4" size={40} />
                  <div className="text-4xl font-bold text-white mb-2">{avantage.stat}</div>
                  <h3 className="text-xl font-semibold text-white mb-3">{avantage.title}</h3>
                  <p className="text-navy-200 font-light text-sm">{avantage.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ExemplesSection />

      <CTASection
        title='Prêt pour une communication <span class="font-semibold text-blue-600">360°</span> ?'
        description="Déployons ensemble une stratégie de communication intégrée et impactante."
        primaryButtonText="Lancer ma campagne 360°"
        whatsappMessage="Bonjour Julycia ! J'aimerais discuter d'un projet de communication 360° avec vous."
      />

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Communication360Page;
